import { ReactNode, Suspense } from "react";
import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import { captureException, ErrorBoundary, withScope } from "@sentry/react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { App, ConfigProvider } from "antd5";

import { AdminBar } from "components/admin_bar/AdminBar";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { IntegrationAPIProvider } from "lib/integrationApiContext";
import { OpenAPIProvider } from "lib/openApiContext";
import { AppTokenProvider } from "lib/providers/ApiTokenProvider";
import { DialogManagerProvider } from "lib/providers/DialogManager";
import { ProHelperProvider } from "lib/providers/ProHelper";
import { ResponsivenessSvcProvider } from "lib/providers/Responsive";
import { retryUnlessUnrecoverable } from "lib/queryRetry";
import { shouldReportToSentry, toReportableError } from "lib/sentryErrors";
import StotlesAPI from "lib/StotlesApi";
import { ApiContextProvider } from "lib/stotlesApiContext";
import { lightTheme } from "../../styles/theme";
import { CurrentUserConfiguredProviders } from "./CurrentUserConfiguredProviders";
import { DefaultTheme } from "./DefaultTheme";
import { ErrorBoundaryFallback } from "./ErrorBoundaryFallback";

const HIDE_ADMIN_BAR_KEY = "hideAdminBar";

const STOTLES_API = new StotlesAPI();
export const REACT_QUERY_CLIENT = new QueryClient({
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      if (!shouldReportToSentry(mutation.meta, err)) {
        return;
      }
      withScope((scope) => {
        scope.setContext("mutation", {
          mutationId: mutation.mutationId,
          variables: mutation.state.variables,
        });
        const { error, fingerprint } = toReportableError(err);
        if (mutation.options.mutationKey) {
          scope.setFingerprint([
            ...(Array.from(mutation.options.mutationKey) as string[]),
            ...(fingerprint ?? []),
          ]);
        } else if (fingerprint) {
          scope.setFingerprint(fingerprint);
        }
        captureException(error);
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (!shouldReportToSentry(query.meta, err)) {
        return;
      }
      withScope((scope) => {
        scope.setContext("query", { queryHash: query.queryHash });
        const { error, fingerprint } = toReportableError(err);
        if (fingerprint) {
          // A query's first key part is its name, which keeps one endpoint's failures apart from another's.
          const [name] = query.queryKey;
          scope.setFingerprint(typeof name === "string" ? [name, ...fingerprint] : fingerprint);
        }
        captureException(error);
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 10000,
      refetchOnWindowFocus: false,
      retry: retryUnlessUnrecoverable,
    },
  },
});

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ThemeProvider theme={lightTheme}>
      <ErrorBoundary
        showDialog
        fallback={(props) => (
          <FullScreenContainer>
            <ErrorBoundaryFallback hideLivechat {...props} />
          </FullScreenContainer>
        )}
      >
        <ConfigProvider prefixCls="ant5" theme={DefaultTheme}>
          <App>
            <Suspense
              fallback={
                <FullScreenContainer data-testid="suspense-fallback">
                  <CentredSpinner />
                </FullScreenContainer>
              }
            >
              <QueryClientProvider client={REACT_QUERY_CLIENT}>
                <ApiContextProvider api={STOTLES_API}>
                  <OpenAPIProvider>
                    <AppTokenProvider>
                      <IntegrationAPIProvider>
                        <CurrentUserConfiguredProviders>
                          <ErrorBoundary
                            showDialog
                            fallback={(props) => (
                              <FullScreenContainer>
                                <ErrorBoundaryFallback {...props} />
                              </FullScreenContainer>
                            )}
                          >
                            <ResponsivenessSvcProvider>
                              <DialogManagerProvider>
                                <ProHelperProvider>
                                  <AdminBar />
                                  {children}
                                </ProHelperProvider>
                              </DialogManagerProvider>
                            </ResponsivenessSvcProvider>
                          </ErrorBoundary>
                        </CurrentUserConfiguredProviders>
                      </IntegrationAPIProvider>
                    </AppTokenProvider>
                  </OpenAPIProvider>
                </ApiContextProvider>
                {sessionStorage.getItem(HIDE_ADMIN_BAR_KEY) !== "true" && (
                  <ReactQueryToggle>
                    <ReactQueryDevtools initialIsOpen={false} position="top-left" />
                  </ReactQueryToggle>
                )}
              </QueryClientProvider>
            </Suspense>
          </App>
        </ConfigProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

const FullScreenContainer = styled.div({
  height: "100vh",
});

const ReactQueryToggle = styled.div({
  "& button": {
    left: "50% !important",
    fontSize: "1em !important",
    position: "absolute !important" as "absolute",
    "& svg": {
      display: "none",
    },
    "&::after": {
      content: '"RQ"',
      fontSize: "0.7em",
    },
  },
  "@media print": {
    display: "none !important" as "none",
  },
});
